import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import {
  Badge,
  Button,
  Callout,
  Label,
  ListEmptyState,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  PencilIcon,
  PlusIcon,
  SaveIcon,
  Switch,
  TextInput,
  XIcon,
} from '../../components/ui';
import { t } from '../../i18n';
import {
  ApiError,
  api,
  type AppFeature,
  type AppPackage,
} from '../../services/api';

function slugifyCode(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

const MAIN_MENU_CODES = new Set([
  'dashboard',
  'customers',
  'invoices',
  'payments',
]);

const SHIPMENT_MENU_CODES = new Set([
  'shipment_files',
  'expense_categories',
  'reports_shipment_monthly_summary',
  'reports_shipment_expense_categories',
  'reports_shipment_open_files',
  'reports_shipment_by_customer',
  'reports_shipment_container_bl',
  'reports_shipment_expense_analysis',
  'reports_shipment_by_port_vessel',
]);

const COMPANY_EXPENSE_MENU_CODES = new Set([
  'company_expenses',
  'company_expense_categories',
  'reports_company_expense_yearly_monthly',
  'reports_company_expense_by_vendor',
  'reports_company_expense_type_analysis',
  'reports_company_expense_payments',
  'reports_company_expense_aging',
]);

const MYDATA_MENU_CODES = new Set(['mydata']);

const REPORT_MENU_CODES = new Set([
  'reports_monthly_summary',
  'reports_yearly_monthly',
  'reports_customer_balances',
  'reports_aging',
  'reports_category_summary',
  'reports_unallocated_payments',
  'reports_agreement_variance',
]);

const SETTINGS_MENU_CODES = new Set([
  'settings',
  'invoice_categories',
  'users',
]);

type FeatureGroup = {
  key: string;
  title: string;
  features: AppFeature[];
};

function groupMenuFeatures(
  features: AppFeature[],
  labels: {
    packageMenuMain: string;
    packageMenuReports: string;
    packageMenuShipmentReports: string;
    packageMenuCompanyExpenses: string;
    packageMenuMydata: string;
    packageMenuSettings: string;
    packageMenuOther: string;
  },
): FeatureGroup[] {
  const main: AppFeature[] = [];
  const reports: AppFeature[] = [];
  const shipment: AppFeature[] = [];
  const companyExpenses: AppFeature[] = [];
  const mydata: AppFeature[] = [];
  const settings: AppFeature[] = [];
  const other: AppFeature[] = [];

  for (const feature of features) {
    if (MAIN_MENU_CODES.has(feature.code)) main.push(feature);
    else if (REPORT_MENU_CODES.has(feature.code)) reports.push(feature);
    else if (SHIPMENT_MENU_CODES.has(feature.code)) shipment.push(feature);
    else if (COMPANY_EXPENSE_MENU_CODES.has(feature.code))
      companyExpenses.push(feature);
    else if (MYDATA_MENU_CODES.has(feature.code)) mydata.push(feature);
    else if (SETTINGS_MENU_CODES.has(feature.code)) settings.push(feature);
    else other.push(feature);
  }

  const groups: FeatureGroup[] = [];
  if (main.length > 0) {
    groups.push({
      key: 'main',
      title: labels.packageMenuMain,
      features: main,
    });
  }
  if (reports.length > 0) {
    groups.push({
      key: 'reports',
      title: labels.packageMenuReports,
      features: reports,
    });
  }
  if (shipment.length > 0) {
    groups.push({
      key: 'shipment',
      title: labels.packageMenuShipmentReports,
      features: shipment,
    });
  }
  if (companyExpenses.length > 0) {
    groups.push({
      key: 'companyExpenses',
      title: labels.packageMenuCompanyExpenses,
      features: companyExpenses,
    });
  }
  if (mydata.length > 0) {
    groups.push({
      key: 'mydata',
      title: labels.packageMenuMydata,
      features: mydata,
    });
  }
  if (settings.length > 0) {
    groups.push({
      key: 'settings',
      title: labels.packageMenuSettings,
      features: settings,
    });
  }
  if (other.length > 0) {
    groups.push({
      key: 'other',
      title: labels.packageMenuOther,
      features: other,
    });
  }
  return groups;
}

export function AppPackagesPage() {
  const labels = t().platform;
  const [packages, setPackages] = useState<AppPackage[]>([]);
  const [features, setFeatures] = useState<AppFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AppPackage | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);

  const featureGroups = useMemo(() => {
    const platform = t().platform;
    return groupMenuFeatures(features, {
      packageMenuMain: platform.packageMenuMain,
      packageMenuReports: platform.packageMenuReports,
      packageMenuShipmentReports: platform.packageMenuShipmentReports,
      packageMenuCompanyExpenses: platform.packageMenuCompanyExpenses,
      packageMenuMydata: platform.packageMenuMydata,
      packageMenuSettings: platform.packageMenuSettings,
      packageMenuOther: platform.packageMenuOther,
    });
  }, [features]);

  async function load() {
    setLoading(true);
    setListError(null);
    try {
      const [pkgs, feats] = await Promise.all([
        api.getAppPackages(),
        api.getAppFeatures(),
      ]);
      setPackages(pkgs);
      setFeatures(feats);
    } catch (err) {
      setListError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function openCreate() {
    setEditing(null);
    setName('');
    setCode('');
    setCodeTouched(false);
    setDescription('');
    setActive(true);
    setSelectedCodes([]);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(pkg: AppPackage) {
    setEditing(pkg);
    setName(pkg.name);
    setCode(pkg.code);
    setCodeTouched(true);
    setDescription(pkg.description ?? '');
    setActive(pkg.active);
    setSelectedCodes(pkg.features.map((f) => f.code));
    setFormError(null);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
    setFormError(null);
  }

  function toggleFeature(codeValue: string) {
    setSelectedCodes((prev) =>
      prev.includes(codeValue)
        ? prev.filter((c) => c !== codeValue)
        : [...prev, codeValue],
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !code.trim()) {
      setFormError(labels.requiredPackageFields);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await api.updateAppPackage(editing.id, {
          name: name.trim(),
          code: code.trim(),
          description: description.trim() || null,
          active,
        });
        await api.setAppPackageFeatures(editing.id, selectedCodes);
        setMessage(labels.packageUpdated);
      } else {
        await api.createAppPackage({
          name: name.trim(),
          code: code.trim(),
          description: description.trim() || undefined,
          active,
          featureCodes: selectedCodes,
        });
        setMessage(labels.packageCreated);
      }
      closeForm();
      await load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-sm text-muted">{labels.packagesHint}</p>
        <Button type="button" onClick={openCreate}>
          <PlusIcon className="mr-1.5 h-4 w-4" />
          {labels.newPackage}
        </Button>
      </div>

      {message ? <Callout variant="success">{message}</Callout> : null}
      {listError ? <Callout variant="danger">{listError}</Callout> : null}

      {loading ? (
        <div className="flex flex-1 items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : packages.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-panel/60">
          <ListEmptyState
            title={labels.packagesEmptyTitle}
            hint={labels.packagesEmptyHint}
            action={
              <Button type="button" onClick={openCreate}>
                <PlusIcon className="mr-1.5 h-4 w-4" />
                {labels.newPackage}
              </Button>
            }
          />
        </div>
      ) : (
        <DataTablePanel className="min-h-0 flex-1">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className={dataTableHeadClass}>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                  <th className="px-4 py-3 font-semibold">{labels.packageName}</th>
                  <th className="px-4 py-3 font-semibold">{labels.packageCode}</th>
                  <th className="px-4 py-3 font-semibold">
                    {labels.packageFeaturesCol}
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    {labels.packageTenantsCol}
                  </th>
                  <th className="px-4 py-3 font-semibold">{labels.colActive}</th>
                  <th className="px-4 py-3 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg) => (
                  <tr key={pkg.id} className="border-b border-line/70">
                    <td className="px-4 py-3.5">
                      <p className="font-medium text-ink">{pkg.name}</p>
                      {pkg.description ? (
                        <p className="text-xs text-muted">{pkg.description}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-xs">{pkg.code}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1">
                        {pkg.features.length === 0 ? (
                          <span className="text-xs text-muted">—</span>
                        ) : (
                          pkg.features.map((f) => (
                            <Badge key={f.id} variant="secondary">
                              {f.nameEl}
                            </Badge>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 tabular-nums">{pkg.tenantCount}</td>
                    <td className="px-4 py-3.5">
                      <Badge variant={pkg.active ? 'success' : 'secondary'}>
                        {pkg.active
                          ? labels.statusActive
                          : labels.statusInactive}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(pkg)}
                      >
                        <PencilIcon className="mr-1 h-3.5 w-3.5" />
                        {labels.editPackageFeatures}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataTablePanel>
      )}

      <Modal isOpen={formOpen} onClose={closeForm} size="lg">
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <ModalHeader>
            <ModalTitle>
              {editing ? labels.editPackageFeatures : labels.newPackage}
            </ModalTitle>
            <Button type="button" variant="ghost" size="sm" onClick={closeForm}>
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-5">
            {formError ? <Callout variant="danger">{formError}</Callout> : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="pkg-name">{labels.packageName}</Label>
                <TextInput
                  id="pkg-name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!codeTouched) {
                      setCode(slugifyCode(e.target.value));
                    }
                  }}
                />
              </div>
              <div>
                <Label htmlFor="pkg-code">{labels.packageCode}</Label>
                <TextInput
                  id="pkg-code"
                  value={code}
                  onChange={(e) => {
                    setCodeTouched(true);
                    setCode(slugifyCode(e.target.value));
                  }}
                />
                <p className="mt-1 text-xs text-muted">{labels.packageCodeHint}</p>
              </div>
            </div>
            <div>
              <Label htmlFor="pkg-desc">{labels.packageDescription}</Label>
              <TextInput
                id="pkg-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                aria-label={labels.packageActive}
              />
              <span className="text-sm text-ink">{labels.packageActive}</span>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-ink">
                  {labels.packageFeatures}
                </p>
                <p className="text-xs text-muted">{labels.packageFeaturesHint}</p>
              </div>
              {featureGroups.map((group) => (
                <div key={group.key} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                    {group.title}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {group.features.map((f) => {
                      const locked = f.alwaysOn;
                      const checked = locked || selectedCodes.includes(f.code);
                      return (
                        <label
                          key={f.id}
                          className={[
                            'flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm',
                            locked
                              ? 'cursor-default bg-surface/40 text-muted'
                              : 'cursor-pointer hover:bg-surface/60',
                          ].join(' ')}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-line text-teal-600 focus:ring-teal-500 disabled:opacity-60"
                            checked={checked}
                            disabled={locked}
                            onChange={() => {
                              if (!locked) toggleFeature(f.code);
                            }}
                          />
                          <span className={locked ? 'text-muted' : 'text-ink'}>
                            {f.nameEl}
                            {locked ? ` (${labels.packageAlwaysOnShort})` : ''}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="ghost" onClick={closeForm}>
              {t().common.cancel}
            </Button>
            <Button type="submit" loading={saving}>
              <SaveIcon className="mr-1.5 h-4 w-4" />
              {t().common.save}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </PageShell>
  );
}
